// Otto's voice: 24 kHz PCM16 from the socket -> resample to the context rate ->
// the player worklet (public/audio/otto-player-worklet.js) -> an analyser (the
// loudness and lip-sync source) -> an optional short delay -> the speakers.
//
// Adapted from ottos-consult src/consult/useGeminiAudioPlayback.js (PORTED-FROM.md).
// Changes for v2 (design 3.2 row 4, 4.3, 7.3):
//   - the worklet reports "playing" and "idle" from the audio thread, replacing
//     the old timer estimate, so speaking, thinking and "his last word played"
//     follow the real sound
//   - duck on barge-in and a 30 ms fade out on a confirmed interruption
//   - analyser smoothing lowered from the browser default 0.8 to 0.4 (most of the
//     mouth lag, research 08 VERIFY rows 35 and 38)
//   - a configurable delay AFTER the analyser (default 50 ms), so the mouth moves
//     with or slightly ahead of the sound; none on Bluetooth, which already
//     trails by about 150 to 250 ms
//   - the lip-sync controller is attached only in video mode (it lives in the
//     lazy 3D chunk), so voice only loads no lip-sync code
import { OTTO_SAMPLE_RATE } from "@otto/protocol";
import { outputRouteFromLatency } from "../../device";
import { audioContextCtor, takePrimedPlaybackContext } from "./audioPrime";
import { pcm16ToFloat32, resampleLinear } from "./pcm";

export const PLAYER_WORKLET_URL = "/audio/otto-player-worklet.js?v=1";

export const PLAYER_TUNING = Object.freeze({
  analyserFftSize: 512,
  /** Browser default is 0.8; 0.3 to 0.5 removes most of the mouth lag (design 7.3). */
  analyserSmoothing: 0.4,
  /** Design 7.3: 40 to 60 ms so the mouth leads the sound a little. 0 turns it off. */
  lipSyncDelayMs: 50,
  /** Design 4.3 and 3.2 row 4. */
  interruptFadeMs: 30,
});

/** What the 3D Otto's lip sync needs from the player; implemented in avatar/lipSync.ts. */
export interface LipSyncAttachment {
  attach(analyser: AnalyserNode): boolean;
  process(): void;
  reset(): void;
}

export type PlayerAudioState = "idle" | "ready" | "blocked" | "failed";

export interface OttoPlayerOptions {
  lipSyncDelayMs?: number;
  onPlaying: (playing: boolean) => void;
  onAudioState: (state: PlayerAudioState) => void;
}

export class OttoPlayer {
  /** 0..1 loudness of what the speakers are playing (RMS x 4), read every frame by both Ottos. */
  readonly levelRef = { current: 0 };
  /** ARKit speech shapes written by the lip sync (video mode only). */
  readonly blendshapeRef: { current: Record<string, number> | null } = { current: null };

  private readonly options: OttoPlayerOptions;
  private context: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private analyser: AnalyserNode | null = null;
  private delay: DelayNode | null = null;
  private lipSync: LipSyncAttachment | null = null;
  private frame = 0;
  private bytes: Uint8Array<ArrayBuffer> | null = null;
  private fadeWaiters: (() => void)[] = [];
  private initPromise: Promise<boolean> | null = null;
  private closed = false;
  playing = false;
  audioState: PlayerAudioState = "idle";

  constructor(options: OttoPlayerOptions) {
    this.options = options;
  }

  get outputLatencySeconds(): number | undefined {
    const latency = (this.context as (AudioContext & { outputLatency?: number }) | null)?.outputLatency;
    return typeof latency === "number" ? latency : undefined;
  }

  get outputRoute(): "bluetooth" | "unknown" {
    return outputRouteFromLatency(this.outputLatencySeconds);
  }

  private setAudioState(state: PlayerAudioState) {
    this.audioState = state;
    this.options.onAudioState(state);
  }

  /** Build the graph once. Safe to call repeatedly. */
  init(): Promise<boolean> {
    if (!this.initPromise) this.initPromise = this.build();
    return this.initPromise;
  }

  private async build(): Promise<boolean> {
    const Ctor = audioContextCtor();
    if (!Ctor || typeof AudioWorkletNode === "undefined") {
      this.setAudioState("failed");
      return false;
    }
    try {
      const context = takePrimedPlaybackContext() ?? new Ctor();
      this.context = context;
      await context.audioWorklet.addModule(PLAYER_WORKLET_URL);
      const node = new AudioWorkletNode(context, "otto-player", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      const analyser = context.createAnalyser();
      analyser.fftSize = PLAYER_TUNING.analyserFftSize;
      analyser.smoothingTimeConstant = PLAYER_TUNING.analyserSmoothing;
      node.connect(analyser);
      const wanted = this.options.lipSyncDelayMs ?? PLAYER_TUNING.lipSyncDelayMs;
      // No extra delay on Bluetooth: the ear already hears Otto late (design 7.3).
      const delayMs = this.outputRoute === "bluetooth" ? 0 : Math.max(0, Math.min(200, wanted));
      if (delayMs > 0) {
        const delay = context.createDelay(0.25);
        delay.delayTime.value = delayMs / 1000;
        analyser.connect(delay);
        delay.connect(context.destination);
        this.delay = delay;
      } else {
        analyser.connect(context.destination);
      }
      node.port.onmessage = (event: MessageEvent) => this.onWorkletMessage(event.data as { type?: string });
      this.node = node;
      this.analyser = analyser;
      this.bytes = new Uint8Array(analyser.fftSize);
      if (this.lipSync) this.lipSync.attach(analyser);
      if (context.state === "suspended") await context.resume().catch(() => undefined);
      this.setAudioState(context.state === "running" ? "ready" : "blocked");
      return true;
    } catch {
      this.setAudioState("failed");
      return false;
    }
  }

  private onWorkletMessage(data: { type?: string }) {
    if (data.type === "playing") {
      this.playing = true;
      this.startLevelLoop();
      this.options.onPlaying(true);
    } else if (data.type === "idle") {
      this.playing = false;
      this.options.onPlaying(false);
    } else if (data.type === "faded") {
      const waiters = this.fadeWaiters;
      this.fadeWaiters = [];
      waiters.forEach((resolve) => resolve());
      this.lipSync?.reset();
    }
  }

  /** Queue one chunk of Otto's 24 kHz PCM16. */
  play(pcm: ArrayBuffer): boolean {
    const context = this.context;
    const node = this.node;
    if (!context || !node) return false;
    if (context.state !== "running") {
      this.setAudioState("blocked");
      return false;
    }
    const samples = resampleLinear(pcm16ToFloat32(pcm), OTTO_SAMPLE_RATE, context.sampleRate);
    node.port.postMessage({ type: "audio", samples }, [samples.buffer]);
    return true;
  }

  duck(gain: number, ms = 40) {
    this.node?.port.postMessage({ type: "duck", gain, ms });
  }

  unduck(ms = 120) {
    this.node?.port.postMessage({ type: "unduck", ms });
  }

  /** Fade Otto out (a confirmed barge-in) and drop what is queued. Resolves when silent. */
  fadeOut(ms: number = PLAYER_TUNING.interruptFadeMs): Promise<void> {
    if (!this.node) return Promise.resolve();
    return new Promise((resolve) => {
      this.fadeWaiters.push(resolve);
      this.node?.port.postMessage({ type: "fadeOut", ms });
    });
  }

  clear() {
    this.node?.port.postMessage({ type: "clear" });
    this.lipSync?.reset();
  }

  /** The 3D Otto attaches its lip sync here while it is on screen. */
  setLipSync(lipSync: LipSyncAttachment | null) {
    this.lipSync?.reset();
    this.lipSync = lipSync;
    this.blendshapeRef.current = null;
    if (lipSync && this.analyser) lipSync.attach(this.analyser);
  }

  /** After a phone lock or background tab. Returns true when sound can play. */
  async resume(): Promise<boolean> {
    const context = this.context;
    if (!context) return false;
    if (context.state !== "running") await context.resume().catch(() => undefined);
    const running = context.state === "running";
    this.setAudioState(running ? "ready" : "blocked");
    return running;
  }

  // Loudness every animation frame while Otto plays, plus a short tail so the
  // mouth closes cleanly. Computed straight from the analyser bytes, with no
  // per-frame allocation (research 08 section 2.6 item 6).
  private startLevelLoop() {
    if (this.frame || !this.analyser || typeof requestAnimationFrame !== "function") return;
    let quietFrames = 0;
    const tick = () => {
      const analyser = this.analyser;
      const bytes = this.bytes;
      if (!analyser || !bytes || this.closed) {
        this.frame = 0;
        return;
      }
      analyser.getByteTimeDomainData(bytes);
      let sum = 0;
      for (let index = 0; index < bytes.length; index += 1) {
        const value = ((bytes[index] ?? 128) - 128) / 128;
        sum += value * value;
      }
      this.levelRef.current = Math.min(1, Math.sqrt(sum / bytes.length) * 4);
      this.lipSync?.process();
      quietFrames = this.playing ? 0 : quietFrames + 1;
      if (quietFrames > 20) {
        this.levelRef.current = 0;
        this.lipSync?.reset();
        this.frame = 0;
        return;
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  close() {
    this.closed = true;
    if (this.frame && typeof cancelAnimationFrame === "function") cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.lipSync?.reset();
    this.lipSync = null;
    if (this.node) this.node.port.onmessage = null;
    this.node?.disconnect();
    this.analyser?.disconnect();
    this.delay?.disconnect();
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.node = null;
    this.analyser = null;
    this.delay = null;
    this.fadeWaiters.forEach((resolve) => resolve());
    this.fadeWaiters = [];
  }
}
